import React from 'react'
import type { FieldError } from 'react-hook-form'
import { cn } from '@/lib/utils'
import LcIcon from '@/components/livecontact/icons'

type CheckboxProps = {
  className?: string
  classLabel?: string
  label?: string | React.ReactNode
  value: boolean
  onChange?: (value: boolean) => void
  reverse?: boolean
  disabled?: boolean
  required?: boolean
  error?: FieldError
  readOnly?: boolean
}

/** 16px checkbox: hairline ring at rest, brand fill when checked; the check pops in at 160ms. */
const Checkbox = ({
  className,
  label,
  value,
  onChange,
  reverse,
  disabled,
  required,
  readOnly,
  error,
  classLabel,
}: CheckboxProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly || disabled) return
    onChange?.(e.target.checked)
  }

  return (
    <label
      className={cn(
        'group relative flex select-none items-center tap-highlight-color',
        reverse && 'flex-row-reverse',
        disabled || readOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        className
      )}
    >
      <input
        type="checkbox"
        className="peer absolute size-0 opacity-0"
        checked={value}
        onChange={handleChange}
        disabled={disabled}
        required={required}
        readOnly={readOnly}
      />

      <span
        className={cn(
          'relative flex size-4 shrink-0 items-center justify-center rounded-[4px] bg-surface',
          'transition-[background-color,box-shadow,transform] duration-[160ms] ease-out-strong group-active:scale-[0.92]',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-brand/40 peer-focus-visible:ring-offset-2',
          value
            ? 'bg-brand shadow-[0_0_0_1px_rgb(var(--lc-brand))]'
            : 'shadow-[0_0_0_1px_rgb(var(--lc-line-2))] group-hover:shadow-[0_0_0_1px_rgb(var(--lc-brand)/0.6)]',
          error && !value && 'shadow-[0_0_0_1px_#EF4444]'
        )}
      >
        <LcIcon
          name="check"
          size={11}
          strokeWidth={2.5}
          className={cn(
            'text-brand-ink transition-[opacity,transform] duration-[160ms] ease-out-strong',
            value ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
          )}
        />
      </span>

      {label && (
        <span
          className={cn(
            'pl-2.5 text-[14px] leading-[1.4] tracking-[-0.2px] text-ink-2',
            reverse && 'mr-auto pr-2.5',
            classLabel,
            error && 'text-[#D33]'
          )}
        >
          {label}
        </span>
      )}
    </label>
  )
}

export default Checkbox
